import React, { useEffect, useRef, useState } from "react";
import { Calendar } from "lucide-react";

/**
 * Shared professional Date Range picker.
 *
 * Emits `onChange(fromYMD, toYMD)` with inclusive dates in `YYYY-MM-DD`
 * form (platform-local calendar dates). Presets and a custom From/To
 * field are rendered inside a popover triggered by a single input-like
 * button. Values are validated (start ≤ end, both required) before Apply
 * is enabled.
 *
 * Usage:
 *   <DateRangePopover from={dateFrom} to={dateTo} onChange={(f, t) => …} />
 */

export function localDate(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function presetRange(key) {
  const now = new Date();
  const today = localDate(now);
  const back = (days) => {
    const d = new Date(now);
    d.setDate(d.getDate() - days);
    return localDate(d);
  };
  const monthsBack = (m) => {
    const d = new Date(now);
    d.setMonth(d.getMonth() - m);
    return localDate(d);
  };
  switch (key) {
    case "today": return [today, today];
    case "yesterday": return [back(1), back(1)];
    case "7d": return [back(6), today];
    case "30d": return [back(29), today];
    case "this_month": {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return [localDate(s), today];
    }
    case "last_month": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return [localDate(s), localDate(e)];
    }
    case "3m": return [monthsBack(3), today];
    case "6m": return [monthsBack(6), today];
    case "12m": return [monthsBack(12), today];
    default: return [today, today];
  }
}

function PresetBtn({ children, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="block w-full text-left h-8 px-3 rounded text-sm text-slate-700 hover:bg-[#EDF5FF] hover:text-[#0F62FE]"
    >
      {children}
    </button>
  );
}

export default function DateRangePopover({ from, to, onChange, testidPrefix = "date-range" }) {
  const [open, setOpen] = useState(false);
  const [tmpFrom, setTmpFrom] = useState(from || "");
  const [tmpTo, setTmpTo] = useState(to || "");
  const [error, setError] = useState("");
  const ref = useRef(null);

  useEffect(() => { setTmpFrom(from || ""); setTmpTo(to || ""); }, [from, to]);

  useEffect(() => {
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, []);

  const applyPreset = (key) => {
    const [f, tt] = presetRange(key);
    setTmpFrom(f); setTmpTo(tt); setError("");
  };

  const validate = (f, t) => {
    if (!f || !t) return "Please pick both dates";
    if (f > t) return "End date must be after start date";
    return "";
  };

  const apply = () => {
    const err = validate(tmpFrom, tmpTo);
    if (err) { setError(err); return; }
    onChange(tmpFrom, tmpTo);
    setOpen(false);
    setError("");
  };

  const displayValue = from && to ? `${from} — ${to}` : "Select date range";
  const canApply = !validate(tmpFrom, tmpTo);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid={`${testidPrefix}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50"
      >
        <Calendar className="w-4 h-4 text-slate-500" />
        <span className="flex-1 text-left">{displayValue}</span>
      </button>
      {open && (
        <div
          data-testid={`${testidPrefix}-popover`}
          className="absolute z-50 mt-1 w-[420px] bg-white rounded-lg border border-slate-200 shadow-lg p-4"
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Presets</div>
              <PresetBtn testid={`${testidPrefix}-preset-today`} onClick={() => applyPreset("today")}>Today</PresetBtn>
              <PresetBtn testid={`${testidPrefix}-preset-yesterday`} onClick={() => applyPreset("yesterday")}>Yesterday</PresetBtn>
              <PresetBtn testid={`${testidPrefix}-preset-7d`} onClick={() => applyPreset("7d")}>Last 7 Days</PresetBtn>
              <PresetBtn testid={`${testidPrefix}-preset-30d`} onClick={() => applyPreset("30d")}>Last 30 Days</PresetBtn>
              <PresetBtn testid={`${testidPrefix}-preset-this-month`} onClick={() => applyPreset("this_month")}>This Month</PresetBtn>
              <PresetBtn testid={`${testidPrefix}-preset-last-month`} onClick={() => applyPreset("last_month")}>Last Month</PresetBtn>
              <PresetBtn testid={`${testidPrefix}-preset-3m`} onClick={() => applyPreset("3m")}>Last 3 Months</PresetBtn>
              <PresetBtn testid={`${testidPrefix}-preset-6m`} onClick={() => applyPreset("6m")}>Last 6 Months</PresetBtn>
              <PresetBtn testid={`${testidPrefix}-preset-12m`} onClick={() => applyPreset("12m")}>Last 12 Months</PresetBtn>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Custom range</div>
              <label className="block text-xs text-slate-500 mb-1">From</label>
              <input
                data-testid={`${testidPrefix}-from`}
                type="date"
                value={tmpFrom || ""}
                onChange={(e) => { setTmpFrom(e.target.value); setError(""); }}
                className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
              <label className="block text-xs text-slate-500 mb-1">To</label>
              <input
                data-testid={`${testidPrefix}-to`}
                type="date"
                value={tmpTo || ""}
                onChange={(e) => { setTmpTo(e.target.value); setError(""); }}
                className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
              {error && (
                <div
                  data-testid={`${testidPrefix}-error`}
                  className="mt-2 text-xs text-rose-600"
                >
                  {error}
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              data-testid={`${testidPrefix}-cancel`}
              onClick={() => { setOpen(false); setError(""); setTmpFrom(from || ""); setTmpTo(to || ""); }}
              className="h-9 px-4 rounded-md border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid={`${testidPrefix}-apply`}
              onClick={apply}
              disabled={!canApply}
              className="h-9 px-4 rounded-md bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
